import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BoxContext = createContext(null);

export const useBox = () => {
  const context = useContext(BoxContext);
  if (!context) {
    throw new Error('useBox must be used within BoxProvider');
  }
  return context;
};

export const BoxProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [accountEmail, setAccountEmail] = useState(null);

  const checkConnectionStatus = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/box/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setIsConnected(data.connected);
        setAccountEmail(data.account_email);
      }
    } catch (err) {
      console.error('Box status check failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkConnectionStatus();
  }, [checkConnectionStatus]);

  const connect = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return { success: false, error: 'Not authenticated' };

    try {
      const res = await fetch(`${API_URL}/api/box/connect`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        window.location.href = data.authorize_url;
        return { success: true };
      } else {
        const err = await res.json();
        return { success: false, error: err.detail || 'Failed to connect' };
      }
    } catch (err) {
      return { success: false, error: 'Network error' };
    }
  }, []);

  const disconnect = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      await fetch(`${API_URL}/api/box/disconnect`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setIsConnected(false);
      setAccountEmail(null);
    } catch (err) {
      console.error('Box disconnect failed:', err);
    }
  }, []);

  const refreshStatus = useCallback(() => {
    checkConnectionStatus();
  }, [checkConnectionStatus]);

  const value = {
    isConnected,
    isLoading,
    accountEmail,
    isConfigured: true,
    connect,
    disconnect,
    refreshStatus
  };

  return (
    <BoxContext.Provider value={value}>
      {children}
    </BoxContext.Provider>
  );
};
